import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEnvelope, FaUser, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import "./SignupForm.css";
const API_URL = process.env.REACT_APP_API_URL;
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/;

const SignupForm = ({ onSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptedTerms: false,
  });
  const [otp, setOtp] = useState("");
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [buttonState, setButtonState] = useState("default");
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isPasswordFieldActive, setIsPasswordFieldActive] = useState(false);

  useEffect(() => {
    if (!showOtpScreen) {
      setResendTimer(30);
      setCanResendOtp(false);
      return;
    }

    if (resendTimer > 0) {
      const timer = window.setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);

      return () => window.clearTimeout(timer);
    }

    setCanResendOtp(true);
  }, [showOtpScreen, resendTimer]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  const getPasswordText = () => {
    if (passwordStrength < 40) return "Weak";
    if (passwordStrength < 70) return "Medium";
    return "Strong";
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.username.trim()) {
      setError("Please enter username");
      return;
    }

    if (!formData.email.trim()) {
      setError("Please enter email");
      return;
    }

    if (!formData.password.trim()) {
      setError("Please enter password");
      return;
    }

    if (!PASSWORD_PATTERN.test(formData.password)) {
      setError(
        "Password must use uppercase, lowercase, numbers, and special characters",
      );
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!formData.acceptedTerms) {
      setError("Please accept the Terms of Service and Privacy Policy");
      return;
    }

    try {
      setButtonState("loading");
      const { acceptedTerms, ...signupPayload } = formData;
      const response = await axios.post(
        `${API_URL}/api/auth/signup`,
        signupPayload
      );

      if (response.status === 200 || response.status === 201) {
        setShowOtpScreen(true);
        setResendTimer(30);
        setCanResendOtp(false);
        setButtonState("default");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Signup failed";

      if (errorMessage === "OTP already sent. Please verify your email.") {
        setShowOtpScreen(true);
        setResendTimer(30);
        setCanResendOtp(false);
        setError("");
        setButtonState("default");
        return;
      }

      setButtonState("error");
      setError(errorMessage);
      setTimeout(() => setButtonState("default"), 900);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("Please enter OTP");
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/auth/verify-otp`,
        {
          email: formData.email,
          otp,
        },
      );

      if (res.status === 200) {
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP");
    }
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    if (onSwitchToLogin) onSwitchToLogin();
  };

  const handleResendOtp = async () => {
    if (!canResendOtp || isResendingOtp) {
      return;
    }

    try {
      setError("");
      setIsResendingOtp(true);

      const response = await axios.post(
        `${API_URL}/api/auth/resend-signup-otp`,
        {
          email: formData.email,
        },
      );

      if (response.status === 200) {
        setResendTimer(30);
        setCanResendOtp(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResendingOtp(false);
    }
  };

  return (
    <div className="signup-component-container">
      <div className="auth-wrapper">
        <div className="background-shape" />

        <div className="welcome-section">
          <div className="welcome-card">
            <div className="welcome-copy">
              <h2>
                Join{" "}
                <span className="brand-wordmark">
                  BlackRoth <sup>TM</sup>
                </span>
              </h2>
              <h4> Where Your Journey Begins</h4>
            </div>
          </div>
        </div>

        <div className="credentials-panel">
          <h2>{showOtpScreen ? "Verify OTP" : "Create your account"}</h2>
          <p className="form-subtitle">
            {showOtpScreen
              ? "Enter the verification code sent to your email to complete signup."
              : "Join the platform and get access to your account experience."}
          </p>

          {!showOtpScreen ? (
            <form onSubmit={handleSubmit}>
              <div className="field-wrapper">
                <input
                  type="text"
                  placeholder=" "
                  value={formData.username}
                  onChange={(e) => handleChange("username", e.target.value)}
                  required
                />
                <label>Full name</label>
                <FaUser className="field-icon" />
              </div>

              <div className="field-wrapper">
                <input
                  type="email"
                  placeholder=" "
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
                <label>Email Address</label>
                <FaEnvelope className="field-icon" />
              </div>

              <div
                className="field-wrapper"
                onFocus={() => setIsPasswordFieldActive(true)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setIsPasswordFieldActive(false);
                  }
                }}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder=" "
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  required
                />
                <label>Password</label>
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <FaEyeSlash size={18} />
                  ) : (
                    <FaEye size={18} />
                  )}
                </button>
              </div>

              <div className="field-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder=" "
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleChange("confirmPassword", e.target.value)
                  }
                  required
                />
                <label>Confirm Password</label>
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash size={18} />
                  ) : (
                    <FaEye size={18} />
                  )}
                </button>
              </div>

              <label className="terms-checkbox-row">
                <input
                  type="checkbox"
                  checked={formData.acceptedTerms}
                  onChange={(e) =>
                    handleChange("acceptedTerms", e.target.checked)
                  }
                  required
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                </span>
              </label>

              {isPasswordFieldActive && (
                <div className="meta-row">
                  <span>Password Strength</span>
                  <span className="strength-tag">{getPasswordText()}</span>
                </div>
              )}

              {error && <p className="error-message">{error}</p>}

              <button
                type="submit"
                className="submit-button"
                disabled={buttonState === "loading"}
              >
                {buttonState === "loading"
                  ? "Creating account..."
                  : "Create account"}
              </button>

              <div className="switch-link">
                Already have an account?
                <button type="button" onClick={handleLoginClick}>
                  Sign in
                </button>
              </div>

            </form>
          ) : (
            <form className="otp-shell" onSubmit={handleVerifyOtp}>
              <p>Enter the OTP sent to {formData.email}</p>
              <input
                className="otp-input"
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />

              {error && <p className="error-message">{error}</p>}

              <div className="otp-resend-row">
                {canResendOtp ? (
                  <button
                    type="button"
                    className="otp-resend-button"
                    onClick={handleResendOtp}
                    disabled={isResendingOtp}
                  >
                    {isResendingOtp ? "Resending OTP..." : "Resend OTP"}
                  </button>
                ) : (
                  <p className="otp-resend-timer">
                    Resend OTP in <span>{resendTimer}s</span>
                  </p>
                )}
              </div>

              <button type="submit" className="otp-button">
                Verify OTP
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
 
